import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Printer, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

const statusColor = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-yellow-100 text-yellow-900 border-yellow-200",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  closed: "bg-blue-100 text-blue-900 border-blue-200",
};

const ALL_STATUSES = ["draft", "in_progress", "completed", "closed"];
const STATUS_LABEL = { draft: "Draft", in_progress: "In Progress", completed: "Completed", closed: "Closed" };

function StatusDropdown({ job, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const change = async (s) => {
    setOpen(false);
    setBusy(true);
    try {
      const { data } = await api.put(`/job-cards/${job.id}`, { ...job, status: s });
      onChange(data);
      toast.success(`Status updated to ${STATUS_LABEL[s]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        onClick={toggle}
        disabled={busy}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-medium cursor-pointer hover:opacity-80 disabled:opacity-50 ${statusColor[job.status] || ""}`}
      >
        {STATUS_LABEL[job.status] || job.status}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            style={{ position: "absolute", top: pos.top, left: pos.left }}
            className="z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[150px]"
          >
            {ALL_STATUSES.filter((s) => s !== job.status).map((s) => (
              <button
                key={s}
                onClick={() => change(s)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
              >
                <span className={`inline-block w-2 h-2 rounded-full border ${statusColor[s]}`} />
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default function JobCardList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    const { data } = await api.get("/job-cards", { params });
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <Layout>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Records</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
            Job Cards
          </h1>
        </div>
        <Link to="/job-cards/new"><Button className="bg-yellow-400 hover:bg-yellow-500 text-slate-900" data-testid="list-new-jc"><Plus className="w-4 h-4 mr-2" /> New Job Card</Button></Link>
      </div>

      <Card className="border-slate-200 mb-4">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex flex-wrap gap-3 p-4 items-end"
        >
          <div className="flex-1 min-w-64">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Job Card No., customer, PO…" className="pl-9 bg-white" data-testid="search-input" />
            </div>
          </div>
          <div className="w-48">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Status</div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-white" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" variant="secondary" data-testid="search-btn">Apply</Button>
        </form>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Job Card No.</th>
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-5 py-3 font-medium">PO</th>
                <th className="px-5 py-3 font-medium">Artwork</th>
                <th className="px-5 py-3 font-medium">Item</th>
                <th className="px-5 py-3 font-medium text-right">Ordered</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="jc-tbody">
              {rows.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-500">No job cards found.</td></tr>
              )}
              {rows.map((j) => (
                <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className="px-5 py-3 font-mono text-slate-900" data-testid="jc-no">{j.job_card_no}</td>
                  <td className="px-5 py-3 break-words">{j.customer_name}</td>
                  <td className="px-5 py-3 text-slate-600 break-words">{j.customer_po}</td>
                  <td className="px-5 py-3 text-slate-600 break-words">{j.artwork_version || "—"}</td>
                  <td className="px-5 py-3 text-slate-600 break-words">{(j.items && j.items[0]?.item_name) || j.item_details?.item_name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-right">{j.ordered_qty}</td>
                  <td className="px-5 py-3">
                    <StatusDropdown job={j} onChange={(updated) => setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r))} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1 flex-nowrap">
                      <Link to={`/job-cards/${j.id}`} className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 whitespace-nowrap">Open</Link>
                      <Link to={`/job-cards/${j.id}/edit`} className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-yellow-100 hover:bg-yellow-200 text-yellow-800 whitespace-nowrap">Edit</Link>
                      <a href={`${API_BASE}/job-cards/${j.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 whitespace-nowrap" data-testid={`print-${j.id}`}><Printer className="w-3 h-3" /> Print</a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
